import Vue from 'vue';
import { memoryDB } from '@/utils/db';
import { sessionBackupDB } from '@/utils/sessionBackupDB';
import { encryptData, decryptData } from '@/utils/crypto';
import { CHARACTER_STORE_URL, BACKUP_API_URL } from '@/api/config';
import {
  computeCloudBackupHash,
  isCloudSyncFatalError,
  isCloudSyncServiceWorkerSupported,
  requestCloudSyncServiceWorker
} from '@/utils/cloudSyncService';
import { generateDefaultModels, generateActiveModelIds } from './modelConfig';
import LZString from 'lz-string';
import { summarizeHistoryAsync } from '@/api/chat';
import { workspaceService } from '@/utils/workspace';

function cleanupSessionWorkspace(sessionId) {
  if (!sessionId) return;
  workspaceService.deleteSessionFiles(sessionId).catch(error => {
    console.error('Failed to clean workspace files for session', error);
  });
}

async function updateDailySessionBackup() {
  try {
    const sessions = await memoryDB.getAllSessions();
    if (sessions.length > 0) {
      await sessionBackupDB.saveDailySnapshot(sessions);
    }
  } catch (error) {
    // Backup failures must not interrupt normal chat persistence.
    console.error('Failed to update local chat history backup:', error);
  }
}

const STORAGE_KEY = 'rainshome_ai_store';
const CLOUD_CONTENT_HASH_KEY = 'cloud_content_md5';
const CLOUD_BACKUP_HASH_KEY = 'cloud_backup_md5';
const MAX_PERSISTED_PAGE_TEXT = 4000;
const MAX_PERSISTED_URL = 2048;
const MAX_PERSISTED_COMMANDS = 8;
const MAX_PERSISTED_COMMAND_TEXT = 300;
const MAX_COMPACT_HISTORY_MESSAGES = 20;
const MAX_COMPACT_MESSAGE_TEXT = 12000;
const MAX_COMPACT_THINKING_TEXT = 4000;
let cloudSyncWorkerEnabled = isCloudSyncServiceWorkerSupported();

const generateChatId = () => 'user_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
const generateMessageId = () => 'msg_' + Date.now().toString(36) + Math.random().toString(36).substr(2);

const normalizeChatId = (id) => {
  if (id === null || id === undefined) return '';
  const normalized = String(id).trim();
  if (!normalized || normalized === 'null' || normalized === 'undefined') return '';
  return normalized;
};

const createDefaultAgentModel = () => ({
  id: 'default-agent',
  name: '浏览器自带模型 (Default)',
  type: 'text',
  endpoint: 'https://api.rainsee.cn/v1/chat/completions',
  apiKey: 'sk-aabaaaaaaaa',
  model: 'rainsee-turbo'
});

const isCurrentAgentMode = () => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('mode') === 'agent';
};

const isTextModel = (model) => !!model && (model.type === 'text' || !model.type);

const findModelById = (models, id) => {
  if (!Array.isArray(models) || !id) return null;
  return models.find(model => model.id === id) || null;
};

const findTextModelById = (models, id) => {
  const model = findModelById(models, id);
  return isTextModel(model) ? model : null;
};

const getFirstTextModel = (models) => {
  if (!Array.isArray(models)) return null;
  return models.find(isTextModel) || null;
};

const normalizeModelOption = (option) => {
  const source = typeof option === 'string' ? { id: option, label: option } : (option || {});
  const id = String(source.id || source.model || '').trim();
  if (!id) return null;

  return {
    id,
    label: String(source.label || id),
    displayName: String(source.displayName || ''),
    ownedBy: String(source.ownedBy || ''),
    pinned: !!source.pinned
  };
};

const normalizeModelConfig = (model) => {
  const config = { ...(model || {}) };
  const rawOptions = Array.isArray(config.modelOptions)
    ? config.modelOptions
    : (config.model ? [config.model] : []);
  const seen = new Set();
  const options = rawOptions
    .map(normalizeModelOption)
    .filter(option => option && !seen.has(option.id) && seen.add(option.id));

  if (config.model && !options.some(option => option.id === String(config.model))) {
    const fallback = normalizeModelOption(config.model);
    if (fallback) options.unshift(fallback);
  }

  config.modelOptions = options;
  config.model = options.find(option => option.id === String(config.model || ''))?.id || options[0]?.id || '';
  config.pinned = !!config.pinned;
  return config;
};

const normalizeModelSettings = (settings) => {
  if (!settings) return settings;
  settings.models = Array.isArray(settings.models)
    ? settings.models.map(normalizeModelConfig)
    : [];
  return settings;
};

const sortModelOptions = (config) => {
  const options = Array.isArray(config && config.modelOptions) ? config.modelOptions : [];
  return options.slice().sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
};

const ensureAgentDefaultModelExists = (models) => {
  if (!Array.isArray(models)) {
    return [createDefaultAgentModel()];
  }

  const builtin = createDefaultAgentModel();
  // 已存在同 endpoint + 同模型的等价配置（如 sioyie 站点内置的 default 模型）时，
  // 不再注入 default-agent，并清理之前已注入的重复项
  const hasEquivalentModel = models.some(model =>
    model.id !== 'default-agent' &&
    isTextModel(model) &&
    model.endpoint === builtin.endpoint &&
    model.model === builtin.model
  );

  if (hasEquivalentModel) {
    return models.filter(model => model.id !== 'default-agent');
  }

  if (!models.find(model => model.id === 'default-agent')) {
    models.unshift(normalizeModelConfig(builtin));
  }

  return models;
};

const applyAgentModeModelSelection = (settings) => {
  if (!settings) return;

  settings.models = ensureAgentDefaultModelExists(settings.models);

  const activeTextModel =
    findTextModelById(settings.models, settings.activeTextModelId) ||
    findTextModelById(settings.models, settings.activeModelId) ||
    findTextModelById(settings.models, 'default-agent') ||
    getFirstTextModel(settings.models);

  const activeTextModelId = activeTextModel ? activeTextModel.id : '';
  settings.activeTextModelId = activeTextModelId;
  settings.activeModelId = activeTextModelId;
};

// 非Agent模式下同样保留内置模型，但保持用户已有的选中模型不变
const ensureDefaultModelAvailable = (settings) => {
  if (!settings) return;

  settings.models = ensureAgentDefaultModelExists(settings.models);

  const activeTextModel =
    findTextModelById(settings.models, settings.activeTextModelId) ||
    findTextModelById(settings.models, settings.activeModelId) ||
    findTextModelById(settings.models, 'default-agent') ||
    getFirstTextModel(settings.models);

  if (activeTextModel) {
    settings.activeTextModelId = activeTextModel.id;
  }

  const activeModel = findModelById(settings.models, settings.activeModelId) || activeTextModel || settings.models[0] || null;
  settings.activeModelId = activeModel ? activeModel.id : '';
};

const getThemeFromUrlNightParam = () => {
  if (typeof window === 'undefined') return null;

  const night = new URLSearchParams(window.location.search).get('night');
  if (night === 'true') return 'dark';
  if (night === 'false') return 'light';
  return null;
};

const truncateText = (value, maxLength) => {
  if (typeof value !== 'string') {
    return value == null ? value : String(value);
  }

  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const normalizePersistedStringArray = (value, maxItems = MAX_PERSISTED_COMMANDS, maxText = MAX_PERSISTED_COMMAND_TEXT) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(item => item != null)
    .map(item => truncateText(String(item), maxText))
    .filter(Boolean)
    .slice(0, maxItems);
};

const sanitizeImageAnalysisForStorage = (imageAnalysis) => {
  if (!imageAnalysis || typeof imageAnalysis !== 'object') {
    return null;
  }

  return {
    kind: truncateText(String(imageAnalysis.kind || 'unknown'), 80),
    summary: truncateText(imageAnalysis.summary || '', MAX_PERSISTED_PAGE_TEXT),
    medical_related: !!imageAnalysis.medical_related,
    document_like: !!imageAnalysis.document_like,
    text_dense: !!imageAnalysis.text_dense,
    should_prioritize_image_qa: !!imageAnalysis.should_prioritize_image_qa,
    keywords: normalizePersistedStringArray(imageAnalysis.keywords, 6, 80)
  };
};

const sanitizePageContextForStorage = (context) => {
  if (!context || typeof context !== 'object') {
    return null;
  }

  return {
    title: truncateText(context.title || '', MAX_PERSISTED_PAGE_TEXT),
    url: truncateText(context.url || '', MAX_PERSISTED_URL),
    content: '',
    imageUrl: truncateText(context.imageUrl || '', MAX_PERSISTED_URL),
    imageAnalysis: sanitizeImageAnalysisForStorage(context.imageAnalysis),
    scene: context.scene != null ? String(context.scene) : '10',
    summary: truncateText(context.summary || '', MAX_PERSISTED_PAGE_TEXT),
    engine: null,
    dynamicCommands: normalizePersistedStringArray(context.dynamicCommands),
    selectText: context.selectText ? truncateText(context.selectText, MAX_PERSISTED_PAGE_TEXT) : null,
    textCommands: normalizePersistedStringArray(context.textCommands)
  };
};

const sanitizeAttachmentForStorage = (attachment) => {
  if (!attachment || typeof attachment !== 'object') {
    return attachment;
  }

  return {
    type: attachment.type || '',
    url: truncateText(attachment.url || '', MAX_PERSISTED_URL),
    name: truncateText(attachment.name || '', 300),
    summary: truncateText(attachment.summary || '', MAX_PERSISTED_PAGE_TEXT)
  };
};

const sanitizeMessageForStorage = (message, compact = false) => {
  if (!message || typeof message !== 'object') {
    return message;
  }

  const sanitized = { ...message };

  if (compact) {
    sanitized.content = truncateText(sanitized.content || '', MAX_COMPACT_MESSAGE_TEXT);
    sanitized.thinking = truncateText(sanitized.thinking || '', MAX_COMPACT_THINKING_TEXT);
  }

  if (Array.isArray(sanitized.attachments)) {
    sanitized.attachments = sanitized.attachments.map(sanitizeAttachmentForStorage);
  }

  if (sanitized.kbContext) {
    delete sanitized.kbContext;
  }

  return sanitized;
};

const sanitizeHistoryForStorage = (history, compact = false) => {
  if (!Array.isArray(history)) {
    return [];
  }

  const messages = compact ? history.slice(-MAX_COMPACT_HISTORY_MESSAGES) : history;
  return messages.map(message => sanitizeMessageForStorage(message, compact));
};

const isQuotaExceededError = (error) => {
  return !!error && (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014
  );
};

const defaultRoles = [
  { id: 'general', name: '全能专家', icon: '🤖', description: '知识渊博，能够回答各种问题', systemPrompt: '你是一个ai助手，能够回答用户的各种问题。', isDefault: true },
  { id: 'lawyer', name: '律师', icon: '⚖️', description: '提供专业的法律咨询', systemPrompt: '你是一位经验丰富的律师，请用专业的法律知识回答用户的问题。', isDefault: true },
  { id: 'teacher', name: '老师', icon: '👩‍🏫', description: '耐心解答，循循善诱', systemPrompt: '你是一位耐心的老师，请用通俗易懂的语言解释复杂的概念。', isDefault: true },
  { id: 'biologist', name: '生物专家', icon: '🧬', description: '解答生物学相关问题', systemPrompt: '你是一位生物学专家，请用专业的生物学知识回答用户的问题。', isDefault: true },
  { id: 'programmer', name: '程序员', icon: '💻', description: '编写代码，解决技术难题', systemPrompt: '你是一位资深的程序员，请提供高效、健壮的代码解决方案。', isDefault: true },
  { id: 'psychologist', name: '心理咨询师', icon: '🧠', description: '倾听烦恼，提供心理疏导', systemPrompt: '你是一位温暖的心理咨询师，请倾听用户的烦恼并提供建议。', isDefault: true },
  { id: 'translator', name: '翻译官', icon: '🔤', description: '专业翻译，支持多种语言', systemPrompt: '你是一位专业的翻译官，请准确地在不同语言之间进行翻译，并保持原意。如果用户发你的不是中文，那你就要翻译成中文；如果用户发你的是中文，没有说明要翻译什么，那就是翻译成英语', isDefault: true },
  { id: 'fitness', name: '健身教练', icon: '🏋️‍♂️', description: '提供运动指导和健身计划', systemPrompt: '你是一位专业的健身教练，请根据用户需求提供科学的锻炼建议和饮食计划。', isDefault: true },
  { id: 'chef', name: '大厨', icon: '👨‍🍳', description: '提供食谱和烹饪技巧', systemPrompt: '你是一位厨艺精湛的大厨，请分享美味的食谱和实用的烹饪技巧。', isDefault: true },
  { id: 'travel', name: '旅行规划师', icon: '✈️', description: '规划行程，推荐景点', systemPrompt: '你是一位资深的旅行规划师，请根据用户的喜好规划完美的旅行行程。', isDefault: true },
  { id: 'finance', name: '理财顾问', icon: '💰', description: '提供投资建议和财务规划', systemPrompt: '你是一位专业的理财顾问，请提供稳健的投资建议和合理的财务规划。', isDefault: true },
  { id: 'parenting', name: '育儿专家', icon: '👶', description: '解答育儿难题，提供建议', systemPrompt: '你是一位经验丰富的育儿专家，请为父母们提供科学的育儿指导和心理支持。', isDefault: true },
  { id: 'historian', name: '历史学家', icon: '📜', description: '讲述历史故事，解析事件', systemPrompt: '你是一位知识渊博的历史学家，请深入浅出地讲解历史知识和文化背景。', isDefault: true },
  { id: 'critic', name: '影评人', icon: '🎬', description: '推荐电影，深入解析剧情', systemPrompt: '你是一位犀利的影评人，请提供深刻的电影分析和有价值的观影推荐。', isDefault: true },
  { id: 'nutritionist', name: '营养师', icon: '🥗', description: '提供健康饮食建议', systemPrompt: '你是一位专业的营养师，请根据用户的身体状况提供健康的饮食方案。', isDefault: true },
  { id: 'career', name: '职业规划师', icon: '💼', description: '职业发展建议和简历优化', systemPrompt: '你是一位资深的职业规划师，请协助用户进行职业定位和简历优化。', isDefault: true },
  { id: 'writer', name: '作家/诗人', icon: '✍️', description: '创作文学作品，润色文字', systemPrompt: '你是一位富有才华的作家，请协助用户进行文学创作或文字润色。', isDefault: true },
  { id: 'marketing', name: '营销专家', icon: '📈', description: '制定营销策略和文案创意', systemPrompt: '你是一位资深的营销专家，请提供有创意的营销方案和吸引人的文案。', isDefault: true },
  { id: 'game', name: '游戏策划', icon: '🎮', description: '设计游戏机制和背景故事', systemPrompt: '你是一位富有想象力的游戏策划，请协助设计有趣的游戏玩法和故事情节。', isDefault: true },
  { id: 'astronomer', name: '天文学家', icon: '🔭', description: '探索宇宙奥秘，解答问题', systemPrompt: '你是一位探索宇宙的天文学家，请用科学的语言揭示星空的奥秘。', isDefault: true },
  { id: 'astrologer', name: '占星师', icon: '🔮', description: '解读星座运势和星盘', systemPrompt: '你是一位神秘的占星师，请为用户解读星座运势和提供心理建议。', isDefault: true },
  { id: 'photographer', name: '摄影师', icon: '📷', description: '提供构图建议和后期技巧', systemPrompt: '你是一位专业的摄影师，请分享构图秘籍和实用的后期修图技巧。', isDefault: true },
  { id: 'language', name: '语言伙伴', icon: '🗣️', description: '练习对话，纠正语法错误', systemPrompt: '你是一位耐心的语言学习伙伴，请与用户进行对话练习并纠正其错误。', isDefault: true },
  { id: 'debate', name: '辩论教练', icon: '🗣️', description: '提升逻辑思维和辩论技巧', systemPrompt: '你是一位逻辑严密的辩论教练，请引导用户提升逻辑分析和表达能力。', isDefault: true },
  { id: 'pm', name: '产品经理', icon: '💡', description: '分析需求，打磨产品逻辑', systemPrompt: '你是一位资深的产品经理，请协助进行需求分析和产品原型设计。', isDefault: true },
  { id: 'designer', name: '室内设计师', icon: '🏠', description: '提供装修建议和空间布局', systemPrompt: '你是一位富有审美的室内设计师，请提供实用的装修灵感和空间规划建议。', isDefault: true },
];

// Default state definition
const defaultModels = generateDefaultModels();

const defaultState = {
  token: localStorage.getItem('token') || '',
  isAgentMode: false,
  theme: 'light', // 'light' | 'dark'
  history: [], // Array of { role: 'user'|'assistant', content: '', thinking: '' }
  isCompressing: false, // Background summary lock
  sessions: [], // Array of { id, title, messages, timestamp, chatId }
  sessionCategories: [], // Array of { id, name, createdAt }
  currentSessionId: null, // ID of the currently loaded session
  chatId: null, // Backend session ID
  roleSettings: {
    activeRoleId: 'general',
    customRoles: [], // Array of { id, name, icon, description, systemPrompt, tags, author, isDefault: false }
    deletedDefaultRoles: [], // Array of IDs of default roles that are hidden/deleted
    roleHistory: [] // Array of role IDs
  },
  // Publicly exposed companion state. The legacy roleSettings block is kept
  // only so existing local data can be migrated without losing user content.
  companionSettings: {
    activeCompanionIds: [],
    customCompanions: [],
    hiddenDefaultCompanions: [],
    companionHistory: []
  },
  onlineStore: {
    cards: [],
    loading: false
  },
  settings: {
    thinkingEnabled: false, // Toggle for "Deep Thinking" mode
    webSearchEnabled: false, // Toggle for "Web Search" mode
    knowledgeBaseAssociationEnabled: true, // Toggle for "Knowledge Base Association" mode
    planEnabled: false, // Toggle for "Plan" mode
    imageGenEnabled: false, // Toggle for "Image Generation" mode
    imageAspectRatio: '1:1', // Image aspect ratio
    imageSize: '1K', // Image size
    cloudSyncEnabled: true, // Toggle for "Cloud Sync"
    backupReminderDays: 1, // Default reminder interval in days
    fontSize: 16, // Default font size for chat messages
    ...generateActiveModelIds(),
    models: defaultModels
  },
  inputContext: '', // Content added via window.addDoc
  attachmentPool: [], // Global pool for attachments: { name, url, summary, type, timestamp }
  soul: { personality: 'You are a helpful AI assistant.' },
  user: { profile: 'User' },
  userInfo: null, // Fetched user info from api
  memory: { longTerm: [] },
  cloudUpdatePending: false,
};

// Load state from localStorage
const loadState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    
    // Decrypt roleSettings if it's a string
    if (parsed.roleSettings && typeof parsed.roleSettings === 'string') {
      parsed.roleSettings = decryptData(parsed.roleSettings) || defaultState.roleSettings;
    }

    // Ignore sessions from localStorage as we use IndexedDB now
    if (parsed.sessions) {
      delete parsed.sessions;
    }

    // Ignore attachmentPool from localStorage as we use IndexedDB now
    if (parsed.attachmentPool) {
      delete parsed.attachmentPool;
    }

    // Keep legacy bulky runtime data out of the localStorage-backed state.
    if (parsed.pageContext) {
      parsed.pageContext = sanitizePageContextForStorage(parsed.pageContext);
    }
    if (parsed.history) {
      parsed.history = sanitizeHistoryForStorage(parsed.history);
    }
    if (parsed.cloudUpdatePending) {
      parsed.cloudUpdatePending = false;
    }
    if (parsed.userInfo) {
      delete parsed.userInfo;
    }

    // token 单独存放在 localStorage['token']，不允许被旧的持久化状态覆盖
    if (parsed.token !== undefined) {
      delete parsed.token;
    }

    // Migration: Migrate old settings structure to new models array
    if (parsed.settings) {
      if (parsed.settings.backupReminderDays === undefined) {
          parsed.settings.backupReminderDays = 1;
      }

      if (!parsed.settings.models) {
        const oldSettings = parsed.settings;
        parsed.settings = {
          thinkingEnabled: oldSettings.thinkingEnabled ?? false,
          webSearchEnabled: oldSettings.webSearchEnabled ?? false,
          knowledgeBaseAssociationEnabled: oldSettings.knowledgeBaseAssociationEnabled ?? true,
          imageGenEnabled: oldSettings.imageGenEnabled ?? false,
          imageAspectRatio: oldSettings.imageAspectRatio ?? '1:1',
          imageSize: oldSettings.imageSize ?? '1K',
          activeModelId: 'default',
          activeTextModelId: 'default',
          activeT2iModelId: '',
          activeI2iModelId: '',
          models: [
            {
              id: 'default',
              name: 'Default Model',
              type: 'text',
              endpoint: oldSettings.endpoint || 'https://api.openai.com/v1/chat/completions',
              apiKey: oldSettings.apiKey || '',
              model: oldSettings.model || 'gpt-3.5-turbo'
            }
          ]
        };
      } else {
        // Ensure type exists on all models
        parsed.settings.models.forEach(m => {
          if (!m.type) m.type = 'text';
        });
        
        // Ensure new active IDs exist
        if (parsed.settings.activeTextModelId === undefined) {
          parsed.settings.activeTextModelId = parsed.settings.activeModelId || 'default';
        }
        if (parsed.settings.activeT2iModelId === undefined) {
          parsed.settings.activeT2iModelId = '';
        }
        if (parsed.settings.activeI2iModelId === undefined) {
          parsed.settings.activeI2iModelId = '';
        }
      }

      normalizeModelSettings(parsed.settings);
    }
    
    return parsed;
  } catch (e) {
    console.error('Failed to load state', e);
    return {};
  }
};

// Initialize state: Merge defaults with saved state
const savedState = loadState();
const legacyRoleSettings = savedState.roleSettings || {};
const savedCompanionSettings = savedState.companionSettings || {};
const initialState = {
  ...defaultState,
  ...savedState,
  roleSettings: {
    ...defaultState.roleSettings,
    ...legacyRoleSettings
  },
  companionSettings: {
    ...defaultState.companionSettings,
    ...savedCompanionSettings,
    activeCompanionIds: Array.isArray(savedCompanionSettings.activeCompanionIds)
      ? savedCompanionSettings.activeCompanionIds
      : (legacyRoleSettings.activeRoleId && legacyRoleSettings.activeRoleId !== 'general'
        ? [legacyRoleSettings.activeRoleId]
        : []),
    customCompanions: Array.isArray(savedCompanionSettings.customCompanions)
      ? savedCompanionSettings.customCompanions
      : (legacyRoleSettings.customRoles || []).map(companion => ({
        ...companion,
        aliases: Array.isArray(companion.aliases) ? companion.aliases : []
      })),
    hiddenDefaultCompanions: Array.isArray(savedCompanionSettings.hiddenDefaultCompanions)
      ? savedCompanionSettings.hiddenDefaultCompanions
      : (legacyRoleSettings.deletedDefaultRoles || []),
    companionHistory: Array.isArray(savedCompanionSettings.companionHistory)
      ? savedCompanionSettings.companionHistory
      : (legacyRoleSettings.roleHistory || [])
  },
  settings: {
    ...defaultState.settings,
    ...(savedState.settings || {}),
    // Always initialize volatile states to default
    imageGenEnabled: defaultState.settings.imageGenEnabled
  }
};

normalizeModelSettings(initialState.settings);
initialState.sessionCategories = Array.isArray(initialState.sessionCategories)
  ? initialState.sessionCategories
      .filter(category => category && category.id && String(category.name || '').trim())
      .map(category => ({ ...category, id: String(category.id), name: String(category.name).trim() }))
  : [];

const urlTheme = getThemeFromUrlNightParam();
if (urlTheme) {
  initialState.theme = urlTheme;
}

// 1. URL Token Logic: Check for token in URL params on initialization
const urlParams = new URLSearchParams(window.location.search);
const tokenFromUrl = urlParams.get('token');
if (tokenFromUrl) {
  initialState.token = tokenFromUrl;
  // 登录回跳只通过 URL 传递一次 token，必须立即写入 localStorage，
  // 否则地址栏清理后刷新页面，登录态就会丢失
  localStorage.setItem('token', tokenFromUrl);
}

// 2. 内置默认模型逻辑：Agent模式下强制选中内置模型，非Agent模式下仅保证其存在
const isAgentMode = isCurrentAgentMode();
initialState.isAgentMode = isAgentMode;

if (isAgentMode) {
  applyAgentModeModelSelection(initialState.settings);
} else {
  ensureDefaultModelAvailable(initialState.settings);
}

initialState.settings.cloudSyncEnabled = initialState.settings.cloudSyncEnabled && cloudSyncWorkerEnabled;

// Create the observable store
export const store = Vue.observable(initialState);
export { defaultRoles };

// Persistence helper
let backupTimeout = null;
const createStateToSave = ({ compactHistory = false, omitHistory = false } = {}) => {
  const stateToSave = {
    isAgentMode: store.isAgentMode,
    theme: store.theme,
    history: omitHistory ? [] : sanitizeHistoryForStorage(store.history, compactHistory),
    currentSessionId: store.currentSessionId,
    sessionCategories: store.sessionCategories,
    chatId: store.chatId,
    roleSettings: store.roleSettings,
    companionSettings: store.companionSettings,
    settings: { ...store.settings },
    inputContext: compactHistory ? truncateText(store.inputContext || '', MAX_COMPACT_MESSAGE_TEXT) : store.inputContext,
    soul: store.soul,
    user: store.user,
    memory: store.memory,
    pageContext: sanitizePageContextForStorage(store.pageContext)
  };

  // Do not persist volatile states
  delete stateToSave.settings.imageGenEnabled;

  // Encrypt roleSettings
  if (stateToSave.roleSettings) {
    stateToSave.roleSettings = encryptData(stateToSave.roleSettings);
  }

  return stateToSave;
};

const scheduleAutoBackup = () => {
  if (!store.token || !cloudSyncWorkerEnabled || !store.settings.cloudSyncEnabled) return;

  if (backupTimeout) clearTimeout(backupTimeout);
  backupTimeout = setTimeout(() => {
    if (mutations && mutations.autoUploadCloudBackup) {
      mutations.autoUploadCloudBackup();
      //数据有更新，触发下载
    }
  }, 5000); // 5 seconds debounce
};

const writeStateToLocalStorage = (state) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const createCloudSyncWorkerInput = () => {
  return {
    isAgentMode: store.isAgentMode,
    theme: store.theme,
    history: store.history,
    currentSessionId: store.currentSessionId,
    sessionCategories: store.sessionCategories,
    chatId: store.chatId,
    roleSettings: store.roleSettings,
    companionSettings: store.companionSettings,
    settings: store.settings,
    inputContext: store.inputContext,
    pageContext: store.pageContext
  };
};

const isCloudBackupSuccess = (result) => {
  return !!result && (
    result.code === 0 ||
    result.status === 'success' ||
    (result.data && result.data.code === 0)
  );
};

const parseLegacyCloudBackupData = (raw) => {
  if (!raw || typeof raw !== 'string') {
    return null;
  }

  try {
    const decompressed = LZString.decompressFromUTF16(raw);
    if (decompressed) {
      return JSON.parse(decompressed);
    }
  } catch (error) {
    // Ignore and continue with raw JSON parsing.
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse cloud data:', error);
    return null;
  }
};

const disableCloudSync = (error, context) => {
  const wasEnabled = cloudSyncWorkerEnabled || store.settings.cloudSyncEnabled;
  if (!wasEnabled) {
    return;
  }

  cloudSyncWorkerEnabled = false;
  store.settings.cloudSyncEnabled = false;
  store.cloudUpdatePending = false;
  localStorage.removeItem(CLOUD_CONTENT_HASH_KEY);
  localStorage.removeItem(CLOUD_BACKUP_HASH_KEY);
  saveState();
  console.warn(`Cloud sync disabled during ${context}; Service Worker is unavailable.`, error);
};

const saveState = () => {
  try {
    writeStateToLocalStorage(createStateToSave());
    scheduleAutoBackup();
  } catch (e) {
    if (!isQuotaExceededError(e)) {
      console.error('Failed to save state', e);
      return;
    }

    try {
      writeStateToLocalStorage(createStateToSave({ compactHistory: true }));
      scheduleAutoBackup();
      console.warn('State exceeded localStorage quota; saved a compact state snapshot instead.');
    } catch (compactError) {
      try {
        localStorage.removeItem(STORAGE_KEY);
        writeStateToLocalStorage(createStateToSave({ compactHistory: true, omitHistory: true }));
        scheduleAutoBackup();
        console.warn('State exceeded localStorage quota; saved settings without chat history.');
      } catch (minimalError) {
        console.error('Failed to save state', minimalError);
      }
    }
  }
};


// Mutations / Actions
export const mutations = {
  ensureCurrentSessionId() {
    if (!store.currentSessionId) {
      store.currentSessionId = String(Date.now());
      saveState();
    }
    return store.currentSessionId;
  },
  // Theme Management
  toggleTheme() {
    store.theme = store.theme === 'light' ? 'dark' : 'light';
    this.applyTheme();
    saveState();
  },
  setTheme(theme) {
    store.theme = theme;
    this.applyTheme();
    saveState();
  },
  applyTheme() {
    // We will use data-theme attribute on document element for CSS variables
    document.documentElement.setAttribute('data-theme', store.theme);
  },

  // Token Management
  setToken(token) {
    store.token = token;
    localStorage.setItem('token', token);
    this.fetchUserInfo();
    this.syncAgentModel();
    this.checkCloudSync(true);
  },

  logout() {
    store.token = '';
    store.userInfo = null;
    
    localStorage.removeItem(CLOUD_CONTENT_HASH_KEY);
    localStorage.removeItem(CLOUD_BACKUP_HASH_KEY);
      
    localStorage.removeItem('token');
    this.syncAgentModel();
  },

  // Helper to sync default-agent model based on current URL agent mode
  syncAgentModel() {
    const currentAgentMode = isCurrentAgentMode();
    store.isAgentMode = currentAgentMode;
    if (currentAgentMode) {
      applyAgentModeModelSelection(store.settings);
    } else {
      ensureDefaultModelAvailable(store.settings);
    }
    saveState();
  },

  ensureDefaultAgentModelOnPageLoad() {
    const currentAgentMode = isCurrentAgentMode();
    store.isAgentMode = currentAgentMode;
    if (currentAgentMode) {
      applyAgentModeModelSelection(store.settings);
    } else {
      ensureDefaultModelAvailable(store.settings);
    }
    saveState();
  },

  // Initialize or get persistent ChatID (User Identity)
  initChatId() {
    let id = normalizeChatId(localStorage.getItem('aihelp_chat_id')) || normalizeChatId(store.chatId);
    if (!id) {
      id = generateChatId();
    }
    localStorage.setItem('aihelp_chat_id', id);
    store.chatId = id;
    return id;
  },

  setChatId(id) {
    // Legacy support or if we want to override
    const normalizedId = normalizeChatId(id) || this.initChatId();
    store.chatId = normalizedId;
    localStorage.setItem('aihelp_chat_id', normalizedId);
    saveState();
  },

  // Settings
  setThinkingEnabled(enabled) {
    store.settings.thinkingEnabled = enabled;
    saveState();
  },
  setWebSearchEnabled(enabled) {
    store.settings.webSearchEnabled = enabled;
    saveState();
  },
  setKnowledgeBaseAssociationEnabled(enabled) {
    store.settings.knowledgeBaseAssociationEnabled = enabled;
    saveState();
  },
  setImageGenEnabled(enabled) {
    store.settings.imageGenEnabled = enabled;
    saveState();
  },
  setImageAspectRatio(ratio) {
    store.settings.imageAspectRatio = ratio;
    saveState();
  },
  setImageSize(size) {
    store.settings.imageSize = size;
    saveState();
  },
  setCloudSyncEnabled(enabled) {
    if (enabled && !cloudSyncWorkerEnabled) {
      store.settings.cloudSyncEnabled = false;
      saveState();
      return;
    }

    store.settings.cloudSyncEnabled = enabled;
    saveState();
  },
  setPlanEnabled(enabled) {
    store.settings.planEnabled = enabled;
    saveState();
  },
  setBackupReminderDays(days) {
    store.settings.backupReminderDays = days;
    saveState();
  },
  setFontSize(size) {
    store.settings.fontSize = size;
    saveState();
  },
  updateLastBackupTime() {
    localStorage.setItem("lastBackupTime",  Date.now());
  },
  setPageContext(context) {
    store.pageContext = context;
    saveState();
  },

  async addAttachmentToPool(attachment) {
    if (!store.currentSessionId) {
      store.currentSessionId = String(Date.now());
    }

    if (!store.attachmentPool) {
      Vue.set(store, 'attachmentPool', []);
    }
    // Check for duplicates
    const existingIdx = store.attachmentPool.findIndex(a => a.url === attachment.url && a.name === attachment.name);
    if (existingIdx === -1) {
      store.attachmentPool.push({
        ...attachment,
        sessionId: store.currentSessionId, // 绑定当前会话ID
        timestamp: Date.now()
      });
      saveState();
    } else {
      // Update existing
      Vue.set(store.attachmentPool, existingIdx, {
        ...store.attachmentPool[existingIdx],
        ...attachment,
        sessionId: store.currentSessionId // 更新为当前会话ID
      });
      saveState();
    }
    // 异步保存到 DB
    try {
      await memoryDB.set('attachmentPool', store.attachmentPool);
    } catch (e) {
      console.error('Failed to save attachmentPool to DB', e);
    }
  },

  async clearAttachmentPool() {
    store.attachmentPool = [];
    saveState();
    try {
      await memoryDB.set('attachmentPool', []);
    } catch (e) {
      console.error('Failed to clear attachmentPool in DB', e);
    }
  },
  setSettings(newSettings) {
    Object.assign(store.settings, newSettings);
    saveState();
  },
  // Model Management
  addModel(model) {
    const newModel = {
      id: Date.now().toString(),
      ...model
    };
    store.settings.models.push(normalizeModelConfig(newModel));
    saveState();
    return store.settings.models[store.settings.models.length - 1];
  },
  updateModel(id, updates) {
    const idx = store.settings.models.findIndex(m => m.id === id);
    if (idx !== -1) {
      Vue.set(store.settings.models, idx, normalizeModelConfig({ ...store.settings.models[idx], ...updates }));
      saveState();
    }
  },
  deleteModel(id) {
 
    
    const idx = store.settings.models.findIndex(m => m.id === id);
    if (idx === -1) return;
    
    store.settings.models.splice(idx, 1);
    
    if (store.settings.activeModelId === id) {
      store.settings.activeModelId = store.settings.models[0]?.id || '';
    }
    if (store.settings.activeTextModelId === id) {
      const fallback = store.settings.models.find(m => (m.type || 'text') === 'text');
      store.settings.activeTextModelId = fallback?.id || '';
    }
    if (store.settings.activeT2iModelId === id) {
      store.settings.activeT2iModelId = store.settings.models.find(m => m.type === 't2i')?.id || '';
    }
    if (store.settings.activeI2iModelId === id) {
      store.settings.activeI2iModelId = store.settings.models.find(m => m.type === 'i2i')?.id || '';
    }
    saveState();
  },
  setActiveModelOption(configId, type, optionId) {
    const config = store.settings.models.find(model => model.id === configId);
    if (!config) return;
    const option = (config.modelOptions || []).find(item => item.id === optionId);
    if (!option) return;

    Vue.set(config, 'model', option.id);
    if (type === 'text') store.settings.activeTextModelId = configId;
    else if (type === 't2i') store.settings.activeT2iModelId = configId;
    else if (type === 'i2i') store.settings.activeI2iModelId = configId;
    store.settings.activeModelId = configId;
    saveState();
  },
  reorderModelConfigs(type, sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const matches = store.settings.models
      .map((model, index) => ({ model, index }))
      .filter(({ model }) => (model.type || 'text') === type);
    const source = matches.findIndex(item => item.model.id === sourceId);
    const target = matches.findIndex(item => item.model.id === targetId);
    if (source < 0 || target < 0) return;

    const ordered = matches.map(item => item.model);
    const [moved] = ordered.splice(source, 1);
    ordered.splice(target, 0, moved);
    ordered.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
    matches.forEach((item, index) => Vue.set(store.settings.models, item.index, ordered[index]));
    saveState();
  },
  reorderModelOptions(configId, sourceId, targetId) {
    const config = store.settings.models.find(model => model.id === configId);
    if (!config || !Array.isArray(config.modelOptions) || sourceId === targetId) return;
    const options = sortModelOptions(config);
    const source = options.findIndex(item => item.id === sourceId);
    const target = options.findIndex(item => item.id === targetId);
    if (source < 0 || target < 0) return;
    const [moved] = options.splice(source, 1);
    options.splice(target, 0, moved);
    Vue.set(config, 'modelOptions', options);
    saveState();
  },
  toggleModelOptionPinned(configId, optionId) {
    const config = store.settings.models.find(model => model.id === configId);
    if (!config || !Array.isArray(config.modelOptions)) return;
    const options = sortModelOptions(config);
    const index = options.findIndex(item => item.id === optionId);
    if (index < 0) return;
    const option = { ...options[index], pinned: !options[index].pinned };
    options.splice(index, 1);
    const firstUnpinned = options.findIndex(item => !item.pinned);
    const insertAt = option.pinned ? 0 : (firstUnpinned < 0 ? options.length : firstUnpinned);
    options.splice(insertAt, 0, option);
    Vue.set(config, 'modelOptions', options);
    saveState();
  },
  toggleModelConfigPinned(configId) {
    const config = store.settings.models.find(model => model.id === configId);
    if (!config) return;
    const nextPinned = !config.pinned;
    Vue.set(config, 'pinned', nextPinned);
    const type = config.type || 'text';
    const matches = store.settings.models
      .map((model, index) => ({ model, index }))
      .filter(({ model }) => (model.type || 'text') === type);
    const current = matches.map(item => item.model).filter(model => model.id !== configId);
    const pinned = current.filter(model => !!model.pinned);
    const unpinned = current.filter(model => !model.pinned);
    const ordered = nextPinned ? [config, ...pinned, ...unpinned] : [...pinned, config, ...unpinned];
    matches.forEach((item, index) => Vue.set(store.settings.models, item.index, ordered[index]));
    saveState();
  },
  setActiveModel(id) {
    store.settings.activeModelId = id;
    
    // Auto-assign to correct type if we know the model's type
    const model = store.settings.models.find(m => m.id === id);
    if (model) {
      if (model.type === 'text') store.settings.activeTextModelId = id;
      else if (model.type === 't2i') store.settings.activeT2iModelId = id;
      else if (model.type === 'i2i') store.settings.activeI2iModelId = id;
    }
    
    saveState();
  },
  setActiveModelByType(id, type) {
    if (type === 'text') store.settings.activeTextModelId = id;
    else if (type === 't2i') store.settings.activeT2iModelId = id;
    else if (type === 'i2i') store.settings.activeI2iModelId = id;
    
    // Also update general active model if it matches the type
    const model = store.settings.models.find(m => m.id === id);
    if (model) {
      store.settings.activeModelId = id;
    }
    
    saveState();
  },

  // Role Management
  ensureCompanionSettings() {
    if (!store.companionSettings) {
      Vue.set(store, 'companionSettings', {
        activeCompanionIds: [],
        customCompanions: [],
        hiddenDefaultCompanions: [],
        companionHistory: []
      });
    }
    if (!Array.isArray(store.companionSettings.activeCompanionIds)) {
      Vue.set(store.companionSettings, 'activeCompanionIds', []);
    }
    if (!Array.isArray(store.companionSettings.customCompanions)) {
      Vue.set(store.companionSettings, 'customCompanions', []);
    }
    if (!Array.isArray(store.companionSettings.hiddenDefaultCompanions)) {
      Vue.set(store.companionSettings, 'hiddenDefaultCompanions', []);
    }
    if (!Array.isArray(store.companionSettings.companionHistory)) {
      Vue.set(store.companionSettings, 'companionHistory', []);
    }
    return store.companionSettings;
  },
  setActiveCompanions(ids) {
    const settings = this.ensureCompanionSettings();
    const normalized = Array.from(new Set((Array.isArray(ids) ? ids : [ids])
      .filter(id => id && id !== 'general'))).slice(0, 3);
    Vue.set(settings, 'activeCompanionIds', normalized);
    const firstId = normalized[0] || 'general';
    store.roleSettings.activeRoleId = firstId;
    const history = settings.companionHistory || [];
    Vue.set(settings, 'companionHistory', [
      ...normalized,
      ...history.filter(id => !normalized.includes(id))
    ].slice(0, 20));
    // Keep the legacy shape synchronized for old components and backups.
    store.roleSettings.roleHistory = settings.companionHistory.slice();
    saveState();
  },
  addCompanion(id) {
    const settings = this.ensureCompanionSettings();
    if (!id || id === 'general') return;
    if (settings.activeCompanionIds.includes(id)) return;
    this.setActiveCompanions([...settings.activeCompanionIds, id]);
    // Keep the mention picker genuinely recent-first when a companion is
    // appended to an existing collaboration chain.
    const history = settings.companionHistory || [];
    Vue.set(settings, 'companionHistory', [
      id,
      ...history.filter(item => item !== id)
    ].slice(0, 20));
    store.roleSettings.roleHistory = settings.companionHistory.slice();
    saveState();
  },
  removeCompanion(id) {
    const settings = this.ensureCompanionSettings();
    this.setActiveCompanions(settings.activeCompanionIds.filter(item => item !== id));
  },
  clearActiveCompanions() {
    this.setActiveCompanions([]);
  },
  setActiveRole(roleId) {
    // Backward-compatible API: selecting a legacy role replaces the chain.
    this.setActiveCompanions(roleId === 'general' ? [] : [roleId]);
  },
  addOrUpdateRole(role) {
    const settings = this.ensureCompanionSettings();
    const companion = { ...role, aliases: Array.isArray(role.aliases) ? role.aliases : [] };
    const idx = settings.customCompanions.findIndex(r => r.name === companion.name);
    if (idx !== -1) {
      companion.id = settings.customCompanions[idx].id;
      Vue.set(settings.customCompanions, idx, { ...settings.customCompanions[idx], ...companion });
    } else {
      settings.customCompanions.push({
        id: companion.id || Date.now().toString(),
        ...companion
      });
    }
    store.roleSettings.customRoles = settings.customCompanions;
    saveState();
  },
  addCustomRole(role) {
    const settings = this.ensureCompanionSettings();
    settings.customCompanions.push({
      id: Date.now().toString(),
      ...role,
      aliases: Array.isArray(role.aliases) ? role.aliases : []
    });
    store.roleSettings.customRoles = settings.customCompanions;
    saveState();
  },
  deleteCustomRole(roleId) {
    const settings = this.ensureCompanionSettings();
    settings.customCompanions = settings.customCompanions.filter(r => r.id !== roleId);
    store.roleSettings.customRoles = settings.customCompanions;
    this.removeCompanion(roleId);
    settings.companionHistory = settings.companionHistory.filter(id => id !== roleId);
    store.roleSettings.roleHistory = settings.companionHistory.slice();
    saveState();
  },

  // Character Card Management
  toggleDefaultRole(roleId) {
    const settings = this.ensureCompanionSettings();
    const index = settings.hiddenDefaultCompanions.indexOf(roleId);
    if (index === -1) {
      settings.hiddenDefaultCompanions.push(roleId);
    } else {
      settings.hiddenDefaultCompanions.splice(index, 1);
    }
    store.roleSettings.deletedDefaultRoles = settings.hiddenDefaultCompanions;
    saveState();
  },
  
  updateCustomRole(role) {
    const settings = this.ensureCompanionSettings();
    const idx = settings.customCompanions.findIndex(r => r.id === role.id);
    if (idx !== -1) {
      // Use Vue.set to ensure reactivity if replacing the object
      Vue.set(settings.customCompanions, idx, { ...settings.customCompanions[idx], ...role });
      store.roleSettings.customRoles = settings.customCompanions;
      saveState();
    }
  },

  importCharacterCard(card) {
    const settings = this.ensureCompanionSettings();
    const companionCard = { ...card, aliases: Array.isArray(card.aliases) ? card.aliases : [] };
    
    // 检查是否有同名伙伴，如果有则覆盖
    const existingIdx = settings.customCompanions.findIndex(r => r.name === companionCard.name);
    
    if (existingIdx !== -1) {
      const existingId = settings.customCompanions[existingIdx].id;
      const newCard = { ...companionCard, id: existingId, isDefault: false };
      Vue.set(settings.customCompanions, existingIdx, { ...settings.customCompanions[existingIdx], ...newCard });
      store.roleSettings.customRoles = settings.customCompanions;
      saveState();
      return newCard;
    } else {
      const newId = 'imported_' + Date.now();
      const newCard = { ...companionCard, id: newId, isDefault: false };
      settings.customCompanions.push(newCard);
      store.roleSettings.customRoles = settings.customCompanions;
      saveState();
      return newCard;
    }
  },

  // Online Store Actions
  async fetchOnlineCards() {
    store.onlineStore.loading = true;
    try {
      const response = await fetch(`${CHARACTER_STORE_URL}?action=list`);
      const data = await response.json();
      if (data.cards) {
        store.onlineStore.cards = data.cards;
      }
    } catch (e) {
      console.error('Failed to fetch online cards', e);
    } finally {
      store.onlineStore.loading = false;
    }
  },

  async shareCard(card) {
    try {
      const response = await fetch(`${CHARACTER_STORE_URL}?action=share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ card })
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error('Failed to share card', e);
      return { error: e.message };
    }
  },

  // Chat History
  addMessage(message) {
    // message: { role: 'user'|'assistant', content: '', thinking: '', timestamp: Date.now(), summaryInfo: '' }
    store.history.push({
      ...message,
      id: message.id || generateMessageId(),
      timestamp: message.timestamp || Date.now()
    });
    saveState();
  },

  async triggerBackgroundCompression() {
    const UNCOMPRESSED_THRESHOLD = 8; // 每积累 8 条未压缩消息，触发一次压缩
    const UNCOMPRESSED_CHAR_THRESHOLD = 12000; // 或者当未压缩消息字符数超过阈值时触发
    const KEEP_RECENT = 4; // 最近 4 条消息始终保留原文，不纳入本次压缩
    const history = store.history;
    
    // Prevent multiple concurrent compression tasks
    if (store.isCompressing || history.length === 0) return;

    // 寻找最近一条已经被压缩过的消息
    let lastSummaryIndex = -1;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].summaryInfo) {
        lastSummaryIndex = i;
        break;
      }
    }

    const startIndex = lastSummaryIndex === -1 ? 0 : lastSummaryIndex + 1;
    const currentIndex = history.length - 1 - KEEP_RECENT;
    const uncompressedCount = currentIndex - startIndex + 1;

    if (uncompressedCount <= 0) return;

    // 计算未压缩消息的总字符数
    let uncompressedCharCount = 0;
    for (let i = startIndex; i <= currentIndex; i++) {
      const msg = history[i];
      uncompressedCharCount += (msg.content || '').length;
      if (msg.attachments && msg.attachments.length > 0) {
        msg.attachments.forEach(att => {
          if (att.type === 'file' && att.content) {
            uncompressedCharCount += att.content.length;
          }
        });
      }
    }

    // 如果未压缩的消息超过轮数阈值，或者字符数超过字数阈值
    if (uncompressedCount >= UNCOMPRESSED_THRESHOLD || uncompressedCharCount >= UNCOMPRESSED_CHAR_THRESHOLD) {
      store.isCompressing = true; // Use store state as lock
      
      // 提取需要压缩的消息区间
      const messagesToCompress = history.slice(startIndex, currentIndex + 1);
      const previousSummary = lastSummaryIndex !== -1 ? history[lastSummaryIndex].summaryInfo : '';
      
      // 调用后台压缩
      try {
        const newSummary = await summarizeHistoryAsync(messagesToCompress, previousSummary);
        
        if (newSummary) {
            // 写入当前对话的最后一条节点上
            this.setHistorySummary(currentIndex, newSummary);
            console.log('[Background Summary] 压缩成功并记录到 index:', currentIndex, newSummary);
        }
      } catch (err) {
        console.error('[Background Summary] 压缩失败:', err);
      } finally {
        store.isCompressing = false;
      }
    }
  },

  setHistorySummary(index, summaryText) {
    if (store.history[index]) {
      // 兼容 Vue 2.x 的响应式设值
      import('vue').then(({ default: Vue }) => {
        Vue.set(store.history[index], 'summaryInfo', summaryText);
        saveState();
        this.saveCurrentSessionToDB();
      });
    }
  },
  updateLastMessage(updates) {
    const lastMsg = store.history[store.history.length - 1];
    if (lastMsg) {
      Object.assign(lastMsg, updates);
    }
  },
  async finalizeLastMessage() {
    console.log('[DEBUG-STORE] finalizeLastMessage started');
    try {
        saveState();
        console.log('[DEBUG-STORE] State saved to localStorage');
        // Auto-save session to IndexedDB after each AI response
        await this.saveCurrentSessionToDB();
        console.log('[DEBUG-STORE] Session saved to IndexedDB');
        
        // 每次 AI 响应完毕，检查是否需要压缩上下文
        this.triggerBackgroundCompression();
    } catch (e) {
        console.error('[DEBUG-STORE] Error in finalizeLastMessage:', e);
    }
  },
  async saveCurrentSessionToDB() {
    console.log('[DEBUG-STORE] saveCurrentSessionToDB called');
    if (store.history.length === 0) {
        console.log('[DEBUG-STORE] History empty, skipping save');
        return;
    }

    let session;
    if (store.currentSessionId) {
      session = store.sessions.find(s => s.id === store.currentSessionId);
      if (session) {
        session.messages = [...store.history];
        session.timestamp = Date.now();
        session.chatId = store.chatId;
        session.companionIds = [...((store.companionSettings && store.companionSettings.activeCompanionIds) || [])];
        if (store.pageContext) {
          session.pageContext = store.pageContext;
        }
      }
    }

    if (!session) {
      // Create new session if it doesn't exist or currentSessionId is null
      let title = 'New Session';
      if (store.pageContext && store.pageContext.summary) {
        title = store.pageContext.summary.substring(0, 30) + (store.pageContext.summary.length > 30 ? '...' : '');
      } else {
        const firstUserMsg = store.history.find(m => m.role === 'user');
        title = firstUserMsg ? firstUserMsg.content.substring(0, 30) + (firstUserMsg.content.length > 30 ? '...' : '') : 'New Session';
      }
      
      session = {
        id: String(store.currentSessionId || Date.now()),
        title: title,
        categoryId: null,
        messages: [...store.history],
        timestamp: Date.now(),
        chatId: store.chatId,
        companionIds: [...((store.companionSettings && store.companionSettings.activeCompanionIds) || [])],
        pageContext: store.pageContext || null
      };
      
      store.currentSessionId = session.id;
      store.sessions.unshift(session);
    }

    console.log('[DEBUG-STORE] Calling memoryDB.saveSession');
    try {
        await memoryDB.saveSession(session);
        console.log('[DEBUG-STORE] memoryDB.saveSession completed');
        await updateDailySessionBackup();
    } catch (e) {
        console.error('[DEBUG-STORE] memoryDB.saveSession failed:', e);
    }
    saveState();
  },
  rollbackTo(index) {
    if (index >= 0 && index < store.history.length) {
      store.history = store.history.slice(0, index);
      saveState();
      this.saveCurrentSessionToDB();
    }
  },
  clearHistory() {
    // We don't need to manually move history to sessions anymore
    // as it's saved to DB after each turn.
    // Just clear the current state.
    cleanupSessionWorkspace(store.currentSessionId);
    store.history = [];
    store.currentSessionId = null;
    this.clearActiveCompanions();
    this.initChatId();
    store.pageContext = null;
    saveState();
  },
  async loadSession(sessionId) {
    const previousSessionId = store.currentSessionId;
    if (previousSessionId && String(previousSessionId) !== String(sessionId)) {
      cleanupSessionWorkspace(previousSessionId);
    }
    const session = store.sessions.find(s => String(s.id) === String(sessionId));
    if (session) {
      store.history = [...session.messages];
      store.currentSessionId = String(sessionId);
      store.chatId = normalizeChatId(session.chatId) || this.initChatId();
      session.chatId = store.chatId;
      this.setActiveCompanions(Array.isArray(session.companionIds) ? session.companionIds : []);
      if (session.pageContext) {
        store.pageContext = session.pageContext;
      }
      saveState();
    }
  },
  async deleteSession(sessionId) {
    if (String(store.currentSessionId) === String(sessionId)) {
      store.currentSessionId = null;
      this.initChatId();
      store.history = [];
    }
    store.sessions = store.sessions.filter(s => String(s.id) !== String(sessionId));
    await memoryDB.deleteSession(sessionId);
    try {
      await workspaceService.deleteSessionFiles(sessionId);
    } catch (error) {
      console.error('Failed to clean workspace files for session', error);
    }
    
    // 清理关联的附件
    if (store.attachmentPool) {
      store.attachmentPool = store.attachmentPool.filter(a => a.sessionId !== String(sessionId));
      try {
        await memoryDB.set('attachmentPool', store.attachmentPool);
      } catch (e) {
        console.error('Failed to clean attachmentPool in DB', e);
      }
    }
    
    saveState();
    await updateDailySessionBackup();
  },
  async renameSession(sessionId, title) {
    const normalizedTitle = String(title || '').trim();
    if (!normalizedTitle) return false;
    const session = store.sessions.find(item => String(item.id) === String(sessionId));
    if (!session) return false;
    await memoryDB.saveSession({ ...session, title: normalizedTitle });
    Vue.set(session, 'title', normalizedTitle);
    saveState();
    await updateDailySessionBackup();
    return true;
  },
  async moveSessionToCategory(sessionId, categoryId) {
    const normalizedCategoryId = categoryId ? String(categoryId) : null;
    if (normalizedCategoryId && !store.sessionCategories.some(category => category.id === normalizedCategoryId)) return false;
    const session = store.sessions.find(item => String(item.id) === String(sessionId));
    if (!session) return false;
    await memoryDB.saveSession({ ...session, categoryId: normalizedCategoryId });
    Vue.set(session, 'categoryId', normalizedCategoryId);
    saveState();
    await updateDailySessionBackup();
    return true;
  },
  createSessionCategory(name) {
    const normalizedName = String(name || '').trim();
    if (!normalizedName) return null;
    if (store.sessionCategories.some(category => category.name.toLocaleLowerCase() === normalizedName.toLocaleLowerCase())) {
      throw new Error('该分类名称已存在');
    }
    const category = { id: `category_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`, name: normalizedName, createdAt: Date.now() };
    store.sessionCategories.push(category);
    saveState();
    return category;
  },
  renameSessionCategory(categoryId, name) {
    const normalizedName = String(name || '').trim();
    if (!normalizedName) return false;
    const category = store.sessionCategories.find(item => item.id === String(categoryId));
    if (!category) return false;
    if (store.sessionCategories.some(item => item.id !== category.id && item.name.toLocaleLowerCase() === normalizedName.toLocaleLowerCase())) {
      throw new Error('该分类名称已存在');
    }
    Vue.set(category, 'name', normalizedName);
    saveState();
    return true;
  },
  async deleteSessionCategory(categoryId, force = false) {
    const normalizedId = String(categoryId);
    const category = store.sessionCategories.find(item => item.id === normalizedId);
    if (!category) return false;
    const categorizedSessions = store.sessions.filter(session => String(session.categoryId || '') === normalizedId);
    if (categorizedSessions.length && !force) return false;
    for (const session of categorizedSessions) {
      await this.deleteSession(session.id);
    }
    store.sessionCategories = store.sessionCategories.filter(item => item.id !== normalizedId);
    saveState();
    return true;
  },
  setCurrentSession() {
    cleanupSessionWorkspace(store.currentSessionId);
    store.currentSessionId = null;
    // store.chatId = null; // Keep chatId persistent
    store.history = []; // Clear history when starting a fresh session
    store.pageContext = null;
    saveState();
  },

  // External Communication
  appendInputContext(text) {
    if (!text) return;
    // Append with newline if not empty
    store.inputContext = store.inputContext 
      ? store.inputContext + '\n' + text 
      : text;
  },
  clearInputContext() {
    store.inputContext = '';
  },

  // Memory Management
  async loadMemory() {
    try {
      const soul = await memoryDB.get('soul');
      const user = await memoryDB.get('user');
      const memory = await memoryDB.get('memory');
      const attachmentPool = await memoryDB.get('attachmentPool');
      
      if (soul) store.soul = soul;
      if (user) store.user = user;
      if (memory) store.memory = memory;
      if (attachmentPool) store.attachmentPool = attachmentPool;

      // Also load sessions from IndexedDB
      const sessions = await memoryDB.getAllSessions();
      if (sessions && sessions.length > 0) {
        sessions.forEach(session => {
          if (session.categoryId === undefined) Vue.set(session, 'categoryId', null);
        });
        store.sessions = sessions;
        await updateDailySessionBackup();
      }
    } catch (e) {
      console.error('Failed to load memory/sessions from DB', e);
    }
  },
  async updateCoreMemory(type, data) {
    // type: 'soul' | 'user' | 'memory'
    if (store[type] !== undefined) {
      store[type] = data;
      await memoryDB.set(type, data);
    }
  },

  async fetchUserInfo() {
    if (!store.token) {
      store.userInfo = null;
      return;
    }
    try {
      const response = await fetch(`https://api.yjllq.com/api/user/getUserInfo?cookie=${store.token}`);
      const result = await response.json();
      if (result.code === 0 && result.data) {
        store.userInfo = result.data;
      } else {
        store.userInfo = null;
      }
    } catch (e) {
      console.error('Failed to fetch user info:', e);
      store.userInfo = null;
    }
  },

  // Auto Backup & Sync
  async autoUploadCloudBackup(force = false) {
    if (!store.token || !cloudSyncWorkerEnabled || !store.settings.cloudSyncEnabled) return;
    if (store.cloudUpdatePending) return; // Block upload if there's a pending cloud update
    
    try {
      const lastContentHash = localStorage.getItem(CLOUD_CONTENT_HASH_KEY) || '';
      const workerResponse = await requestCloudSyncServiceWorker('upload-backup', {
        apiUrl: BACKUP_API_URL,
        token: store.token,
        appState: createCloudSyncWorkerInput(),
        force,
        lastContentHash
      });
      const result = workerResponse.result;
      const contentHash = workerResponse.contentHash || '';

      if (workerResponse.skipped) {
        return;
      }
      
      if (result.logout || result.code === 401) {
        this.logout();
        return;
      }

      if (isCloudBackupSuccess(result)) {
        console.log('Auto backup success',result.md5);
        if (contentHash) {
          localStorage.setItem(CLOUD_CONTENT_HASH_KEY, contentHash);
        }
        if (result.md5) {
            localStorage.setItem(CLOUD_BACKUP_HASH_KEY, result.md5);
        }
        mutations.updateLastBackupTime();
      } else {
        console.error('Auto backup failed:', result.message);
      }
    } catch (e) {
      if (isCloudSyncFatalError(e)) {
        disableCloudSync(e, 'cloud backup upload');
        return;
      }
      console.error('Auto backup error:', e);
    }
  },

  async checkCloudSync(isFirst = false) {
    if (!store.token || !cloudSyncWorkerEnabled || !store.settings.cloudSyncEnabled) return;
    if (store.cloudUpdatePending) return; // Block check if there's already a pending cloud update
    
    try {
      // Get the last known server MD5 from local storage
      const lastMd5 = localStorage.getItem(CLOUD_BACKUP_HASH_KEY) || '';
      const workerResponse = await requestCloudSyncServiceWorker('check-backup', {
        apiUrl: BACKUP_API_URL,
        token: store.token,
        lastMd5
      });
      const result = workerResponse.result;
      let cloudData = workerResponse.cloudData || null;
      const pendingMd5 = workerResponse.md5 || '';
      const contentHash = workerResponse.contentHash || '';

      if (!cloudData && workerResponse.legacyRaw) {
        cloudData = parseLegacyCloudBackupData(workerResponse.legacyRaw);
      }
      
      if (result.logout || result.code === 401) {
        this.logout();
        return;
      }

      if (result.code === 404) {
          // No backup found on server, upload local data
          console.log('No cloud backup found, uploading local data...');
          localStorage.removeItem(CLOUD_BACKUP_HASH_KEY);
          localStorage.removeItem(CLOUD_CONTENT_HASH_KEY);
          await mutations.autoUploadCloudBackup();
          return;
      }

      if (result.code === 0 && result.status === 'success' && result.data) {
         // Found update
         console.log('Found cloud update, syncing...');
         if (!cloudData && result.data.ret) {
             const raw = result.data.ret;
             if (typeof raw === 'string') {
                 cloudData = parseLegacyCloudBackupData(raw);
             } else {
                 cloudData = raw;
             }
         } else if (!cloudData && result.data.store) {
             cloudData = result.data;
         }
         if (cloudData) {
           store.cloudUpdatePending = {
             cloudData,
             md5: pendingMd5 || result.data.md5,
             contentHash
           };
         }
      } else {
         console.log('Cloud sync: No update needed, checking local changes...');
      //  if(isNewSoUpload){
        
      //    await mutations.autoUploadCloudBackup();
      //  }
      }
    } catch (e) {
      if (isCloudSyncFatalError(e)) {
        disableCloudSync(e, 'cloud backup sync check');
        return;
      }
      console.error('Cloud sync error:', e);
    }
  },

  // Backup & Restore
  async getFullBackup() {
    this.updateLastBackupTime();
    const indexedData = await memoryDB.getAll();
    const storeData = { ...store };
    return {
      store: storeData,
      indexed: indexedData,
      timestamp: Date.now(),
      version: '1.0'
    };
  },
  async restoreFromBackup(backup) {
    if (!backup || !backup.store || !backup.indexed) {
      throw new Error('Invalid backup format');
    }
    
    // Handle legacy backups: If sessions are missing in indexedDB backup but present in store backup,
    // migrate them to indexedDB import data.
    const indexedData = { ...backup.indexed };
    if ((!indexedData.sessions || indexedData.sessions.length === 0) && 
        backup.store.sessions && backup.store.sessions.length > 0) {
      indexedData.sessions = backup.store.sessions;
    }

    // Restore store state (reactive update)
    Object.keys(backup.store).forEach(key => {
      // Skip sessions as it will be loaded from DB after import
      if (key === 'sessions') return;

      if (store[key] !== undefined) {
        // [DEBUG-TEMP] 未登录时导出的备份会把 token 覆盖为空，效果等同被退出登录
        if (key === 'token' && store.token && !backup.store.token) {
          console.warn('[token-debug] restoreFromBackup 正在用空 token 覆盖当前登录态', backup.store.token, new Error().stack);
          debugger;
        }
        store[key] = backup.store[key];
      }
    });

    // Restore chat_id to localStorage
    if (store.chatId) {
      localStorage.setItem('aihelp_chat_id', store.chatId);
    }

    // Restore IndexedDB with merge mode enabled for sessions
    await memoryDB.importAll(indexedData, true);
    
    // Reload memory and sessions from DB to ensure store is in sync
    await mutations.loadMemory();

    saveState();
    return true;
  },

  async handleCloudUpdate(action) {
    const pending = store.cloudUpdatePending;
    if (!pending) return;
   
    if (action === 'restore') {
      store.cloudUpdatePending = false; // Clear state first so other logic can proceed
      saveState();
      const cloudData = pending.cloudData;
      await mutations.restoreFromBackup(cloudData);
      
      try {
        const contentHash = pending.contentHash || await computeCloudBackupHash(cloudData);
        if (contentHash) {
          localStorage.setItem(CLOUD_CONTENT_HASH_KEY, contentHash);
        }
      } catch (error) {
        if (isCloudSyncFatalError(error)) {
          disableCloudSync(error, 'cloud backup restore hash sync');
        } else {
          console.error('Failed to persist restored cloud backup hash:', error);
        }
      }
      
      if (pending.md5) {
          localStorage.setItem(CLOUD_BACKUP_HASH_KEY, pending.md5);
      }
      console.log('Cloud sync complete');
    } else if (action === 'overwrite') {
      store.cloudUpdatePending = false; // Clear state first so upload is not blocked
      saveState();
      await mutations.autoUploadCloudBackup(true);
    }
  }
};

// Apply theme immediately
mutations.applyTheme();
// Ensure ChatId is initialized
mutations.initChatId();
// Load memory from IndexedDB(面板会话恢复要等它把 sessions 载入)
export const memoryLoaded = mutations.loadMemory();
// Fetch User Info
mutations.fetchUserInfo();
if (typeof window !== 'undefined') {
  window.addEventListener('aihelp-cloud-sync-unavailable', (event) => {
    disableCloudSync(event.detail || new Error('Cloud sync Service Worker is unavailable.'), 'cloud sync service worker registration');
  });
}
// Re-check agent default model after page load in case restored backups overwrite it
if (document.readyState === 'complete') {
  mutations.ensureDefaultAgentModelOnPageLoad();
} else {
  window.addEventListener('load', () => {
    mutations.ensureDefaultAgentModelOnPageLoad();
  }, { once: true });
}

// 2. Window exposure: window.addDoc
window.addDoc = (text) => {
  mutations.appendInputContext(text);
  // Dispatch a custom event if components need to react immediately beyond reactive state
  window.dispatchEvent(new CustomEvent('rainshome-doc-added', { detail: text }));
};

export default {
  store,
  mutations
};
